import React, { useEffect, useState } from "react"
import LandingButton from "./LandingButton"
import LandingCountyField from "./LandingCountyField"
import LandingFormModal from "./LandingFormModal"
import apiClient from "../../lib/api"

const inputClassName =
  "w-full rounded-xl border border-[#e5e5e5] bg-white px-3.5 py-3 font-dm-sans text-base tracking-wide text-[#0d0d0d] placeholder:text-[#9a9a9a] outline-none transition focus:border-dc-primary-900/40 focus:ring-2 focus:ring-dc-primary-900/10"

const labelClassName =
  "font-dm-sans text-xs font-semibold uppercase tracking-wider text-[#787878]"

type Props = {
  open: boolean
  onClose: () => void
  defaultCounty?: string
}

const emptyForm = (county = "") => ({ name: "", message: "", county })

const LandingTestimonyModal: React.FC<Props> = ({ open, onClose, defaultCounty = "" }) => {
  const [form, setForm] = useState(() => emptyForm(defaultCounty))
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle")

  useEffect(() => {
    if (!open) return
    setForm(emptyForm(defaultCounty))
    setStatus("idle")
    setSubmitting(false)
  }, [open, defaultCounty])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.message.trim()) {
      setStatus("error")
      return
    }

    setSubmitting(true)
    setStatus("idle")

    try {
      await apiClient.createTestimony({
        name: form.name.trim(),
        message: form.message.trim(),
        county: form.county || undefined,
      })
      setStatus("success")
      setForm(emptyForm(defaultCounty))
    } catch (err) {
      console.error("Error submitting testimony:", err)
      setStatus("error")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <LandingFormModal
      open={open}
      onClose={onClose}
      title={status === "success" ? "Testimony submitted" : "Share a Testimony"}
      titleId="testimony-modal-title"
    >
      {status === "success" ? (
        <div className="flex flex-col gap-4">
          <p className="font-dm-sans text-base leading-7 text-[#4a4a4a]">
            Thank you! Your testimony has been submitted. We&apos;re grateful you chose to share what
            God has done.
          </p>
          <LandingButton onClick={onClose} className="w-full">
            Close
          </LandingButton>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label htmlFor="testimony-modal-name" className={labelClassName}>
              Your name (optional)
            </label>
            <input
              id="testimony-modal-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className={inputClassName}
            />
          </div>

          <LandingCountyField
            id="testimony-modal-county"
            value={form.county}
            onChange={(county) => setForm((prev) => ({ ...prev, county }))}
          />

          <div className="flex flex-col gap-2">
            <label htmlFor="testimony-modal-story" className={labelClassName}>
              The story
            </label>
            <textarea
              id="testimony-modal-story"
              value={form.message}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Tell your story..."
              rows={5}
              className={`${inputClassName} resize-none`}
              required
            />
          </div>

          {status === "error" && (
            <p className="font-dm-sans text-sm text-error">
              Please add your story and try again.
            </p>
          )}

          <LandingButton
            type="submit"
            variant="outline-primary"
            className="w-full uppercase"
            disabled={submitting}
          >
            {submitting ? "Sharing…" : "Share testimony"}
          </LandingButton>
        </form>
      )}
    </LandingFormModal>
  )
}

export default LandingTestimonyModal
